import { useRef, useState } from 'react';
import { AwsClientOverrides, useAwsEffect, useRegion, useSnackbar } from '@localstack/services';
import { LAMBDA_WEB_HELLO_WORLD_ZIP_BASE64 } from '@localstack/constants';
import { LambdaWebFunctionCreateRequest, LambdaWebFunctionCreateOptions, LambdaWebS3Object } from '@localstack/types';

import { buildWebFunctionRequest } from '../utils/webFunctionCreate';

interface PreparedResources {
  id: string;
  bucket?: string;
  bucketVersioningEnabled?: boolean;
  roleArn?: string;
}

interface WebFunctionCreateResult {
  create: (
    data: LambdaWebFunctionCreateRequest,
    options: LambdaWebFunctionCreateOptions,
  ) => Promise<string | undefined>;
  isLoading: boolean;
  hasError: boolean;
  preparedResources?: PreparedResources;
}

export const useWebFunctionCreate = (clientOverrides?: AwsClientOverrides): WebFunctionCreateResult => {
  const { region } = useRegion();
  const { showSnackbar } = useSnackbar();
  const resources = useRef<PreparedResources>();
  const submitting = useRef(false);
  // File reading is the only asynchronous step not covered by useAwsEffect's loading state.
  const [readingFile, setReadingFile] = useState(false);
  const {
    createWebFunction,
    isLoading: creatingFunction,
    hasError: functionError,
  } = useAwsEffect('LambdaWeb', ['createWebFunction'], {
    clientOverrides,
    // A recreated function can reuse its name, but has new endpoints and revisions.
    revalidate: [
      'listWebFunctions',
      'getWebFunction',
      'listWebFunctionEndpoints',
      'getWebFunctionEndpoint',
      'listWebFunctionRevisions',
      'getWebFunctionRevision',
    ],
  });
  const {
    createBucket,
    putBucketVersioning,
    putObject,
    isLoading: preparingStorage,
    hasError: storageError,
  } = useAwsEffect('S3', ['createBucket', 'putBucketVersioning', 'putObject'], {
    clientOverrides: { s3ForcePathStyle: true, ...clientOverrides },
  });
  const {
    createRole,
    putRolePolicy,
    isLoading: preparingRole,
    hasError: roleError,
  } = useAwsEffect('IAM', ['createRole', 'putRolePolicy'], { clientOverrides });

  const prepareCode = async (
    data: LambdaWebFunctionCreateRequest,
    { codeSource, zipFile, storageBucket }: LambdaWebFunctionCreateOptions,
    prepared: PreparedResources,
  ): Promise<LambdaWebS3Object | undefined> => {
    if (codeSource === 's3') {
      return data.revisionConfig.buildConfig.codeConfig?.s3Object;
    }

    // Read before provisioning: an unreadable file must not leave a new bucket or role behind.
    let body: Uint8Array;
    if (codeSource === 'upload') {
      if (!zipFile) return undefined;
      setReadingFile(true);
      try {
        body = new Uint8Array(await zipFile.arrayBuffer());
      } catch {
        showSnackbar({ severity: 'error', message: 'Could not read the ZIP file. Select it again and retry.' });
        return undefined;
      } finally {
        setReadingFile(false);
      }
    } else {
      body = Uint8Array.from(atob(LAMBDA_WEB_HELLO_WORLD_ZIP_BASE64), (character) => character.charCodeAt(0));
    }

    let bucket = storageBucket || prepared.bucket;
    if (!bucket) {
      bucket = `web-function-code-${prepared.id}`;
      const selectedRegion = clientOverrides?.region ?? region;
      if (
        !(await createBucket({
          Bucket: bucket,
          ...(selectedRegion !== 'us-east-1' && {
            CreateBucketConfiguration: { LocationConstraint: selectedRegion },
          }),
        }))
      ) {
        return undefined;
      }
      prepared.bucket = bucket;
    }
    // REFERENCE storage requires versioning. Only configure buckets provisioned by this flow.
    if (bucket === prepared.bucket && !prepared.bucketVersioningEnabled) {
      if (!(await putBucketVersioning({ Bucket: bucket, VersioningConfiguration: { Status: 'Enabled' } }))) {
        return undefined;
      }
      prepared.bucketVersioningEnabled = true;
    }
    const key = `web-functions/${data.functionName}/${crypto.randomUUID()}.zip`;
    const uploaded = await putObject({ Bucket: bucket, Key: key, Body: body, ContentType: 'application/zip' });
    return uploaded ? { bucket, key, versionId: uploaded.VersionId } : undefined;
  };

  const prepareExecutionRole = async (
    data: LambdaWebFunctionCreateRequest,
    { useExistingRole, useKmsKey }: LambdaWebFunctionCreateOptions,
    code: LambdaWebS3Object,
    prepared: PreparedResources,
  ): Promise<string | undefined> => {
    if (useExistingRole) return data.revisionConfig.serviceConfig?.executionRoleArn;
    if (!prepared.roleArn) {
      const created = await createRole({
        RoleName: `web-function-${prepared.id}`,
        AssumeRolePolicyDocument: JSON.stringify({
          Version: '2012-10-17',
          Statement: [{ Effect: 'Allow', Principal: { Service: 'lambda.amazonaws.com' }, Action: 'sts:AssumeRole' }],
        }),
      });
      if (!created?.Role?.Arn) return undefined;
      prepared.roleArn = created.Role.Arn;
    }
    const partition = prepared.roleArn.split(':')[1];
    const policy = await putRolePolicy({
      RoleName: `web-function-${prepared.id}`,
      PolicyName: 'WebFunctionExecution',
      PolicyDocument: JSON.stringify({
        Version: '2012-10-17',
        Statement: [
          {
            Effect: 'Allow',
            Action: ['logs:CreateLogGroup', 'logs:CreateLogStream', 'logs:PutLogEvents'],
            Resource: `arn:${partition}:logs:*:*:*`,
          },
          {
            Effect: 'Allow',
            Action: ['s3:GetObject', 's3:GetObjectVersion'],
            // Only our newly provisioned bucket gets prefix access for subsequent ZIP revisions.
            Resource: `arn:${partition}:s3:::${code.bucket}/${
              prepared.bucket === code.bucket ? `web-functions/${data.functionName}/*` : code.key
            }`,
          },
          ...(useKmsKey ? [{ Effect: 'Allow', Action: ['kms:Decrypt'], Resource: data.revisionConfig.kmsKeyArn }] : []),
        ],
      }),
    });
    return policy ? prepared.roleArn : undefined;
  };

  const create: WebFunctionCreateResult['create'] = async (data, options) => {
    if (submitting.current) return undefined;
    submitting.current = true;
    try {
      const prepared = resources.current ?? { id: crypto.randomUUID() };
      resources.current = prepared;
      const code = await prepareCode(data, options, prepared);
      if (!code) return undefined;
      const roleArn = await prepareExecutionRole(data, options, code, prepared);
      if (!roleArn) return undefined;
      const created = await createWebFunction(buildWebFunctionRequest(data, options, code, roleArn));
      if (created && !created.functionName) {
        showSnackbar({
          severity: 'error',
          message: 'The service did not confirm creation. Check the function list before retrying.',
        });
      }
      return created?.functionName;
    } finally {
      submitting.current = false;
    }
  };

  return {
    create,
    isLoading: readingFile || creatingFunction || preparingStorage || preparingRole,
    hasError: functionError || storageError || roleError,
    preparedResources: resources.current,
  };
};
